import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "mk_chat_enabled_channels";

export function loadEnabledChannelCodes(allCodes: string[]): Set<string> {
  if (allCodes.length === 0) return new Set();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set(allCodes);
    const parsed = JSON.parse(raw) as string[];
    const filtered = parsed.filter((code) => allCodes.includes(code));
    return filtered.length > 0 ? new Set(filtered) : new Set(allCodes);
  } catch {
    return new Set(allCodes);
  }
}

export function saveEnabledChannelCodes(enabled: Set<string>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...enabled]));
}

export function useChatChannelFilters(channelCodes: string[]) {
  const [enabledCodes, setEnabledCodes] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (channelCodes.length === 0) return;
    setEnabledCodes(loadEnabledChannelCodes(channelCodes));
  }, [channelCodes.join(",")]);

  const allEnabled = useMemo(
    () => channelCodes.length > 0 && channelCodes.every((code) => enabledCodes.has(code)),
    [channelCodes, enabledCodes],
  );

  const activeChannelCodes = useMemo(
    () => channelCodes.filter((code) => enabledCodes.has(code)),
    [channelCodes, enabledCodes],
  );

  const toggleChannel = useCallback(
    (code: string) => {
      setEnabledCodes((prev) => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        saveEnabledChannelCodes(next);
        return next;
      });
    },
    [],
  );

  const toggleAll = useCallback(() => {
    setEnabledCodes((prev) => {
      const next = channelCodes.every((code) => prev.has(code))
        ? new Set<string>()
        : new Set(channelCodes);
      saveEnabledChannelCodes(next);
      return next;
    });
  }, [channelCodes]);

  return {
    enabledCodes,
    activeChannelCodes,
    allEnabled,
    toggleChannel,
    toggleAll,
  };
}
